import { motion } from "framer-motion";
import { Info, RotateCcw } from "lucide-react";

interface ProjectionControlsProps {
  numberOfPods: number;
  setNumberOfPods: (value: number) => void;
  pricePerHour: number;
  setPricePerHour: (value: number) => void;
  utilization: number;
  setUtilization: (value: number) => void;
  onReset: () => void;
}

const ProjectionControls = ({
  numberOfPods,
  setNumberOfPods,
  pricePerHour,
  setPricePerHour,
  utilization,
  setUtilization,
  onReset,
}: ProjectionControlsProps) => {
  return (
    <div className="bg-card rounded-2xl p-8 shadow-lg border border-border h-full">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-funnel font-semibold text-2xl text-card-foreground">
          Controls
        </h2>
        <button
          onClick={onReset}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Reset
        </button>
      </div>

      <div className="space-y-8">
        {/* Number of Pods */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="font-medium text-card-foreground flex items-center gap-2">
              Number of Pods
              <div className="group relative">
                <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                <div className="absolute left-0 top-6 w-64 bg-background text-foreground text-xs p-3 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 border border-border">
                  Total number of Qwip pods deployed across locations
                </div>
              </div>
            </label>
            <motion.span
              key={numberOfPods}
              initial={{ scale: 1.2, color: "hsl(var(--brand))" }}
              animate={{ scale: 1, color: "hsl(var(--card-foreground))" }}
              className="font-funnel font-bold text-2xl"
            >
              {numberOfPods}
            </motion.span>
          </div>
          <input
            type="range"
            min="1"
            max="100"
            value={numberOfPods}
            onChange={(e) => setNumberOfPods(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider-thumb"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>1</span>
            <span>100</span>
          </div>
        </div>

        {/* Price per Hour */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="font-medium text-card-foreground flex items-center gap-2">
              Price per Hour
              <div className="group relative">
                <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                <div className="absolute left-0 top-6 w-64 bg-background text-foreground text-xs p-3 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 border border-border">
                  Hourly rate charged to users for pod booking
                </div>
              </div>
            </label>
            <motion.span
              key={pricePerHour}
              initial={{ scale: 1.2, color: "hsl(var(--brand))" }}
              animate={{ scale: 1, color: "hsl(var(--card-foreground))" }}
              className="font-funnel font-bold text-2xl"
            >
              £{pricePerHour.toFixed(2)}
            </motion.span>
          </div>
          <input
            type="range"
            min="5"
            max="15"
            step="0.5"
            value={pricePerHour}
            onChange={(e) => setPricePerHour(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider-thumb"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>£5</span>
            <span>£15</span>
          </div>
        </div>

        {/* Utilization */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="font-medium text-card-foreground flex items-center gap-2">
              Average Utilization
              <div className="group relative">
                <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                <div className="absolute left-0 top-6 w-64 bg-background text-foreground text-xs p-3 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 border border-border">
                  Percentage of available hours that pods are booked and in use
                </div>
              </div>
            </label>
            <motion.span
              key={utilization}
              initial={{ scale: 1.2, color: "hsl(var(--brand))" }}
              animate={{ scale: 1, color: "hsl(var(--card-foreground))" }}
              className="font-funnel font-bold text-2xl"
            >
              {utilization}%
            </motion.span>
          </div>
          <input
            type="range"
            min="10"
            max="80"
            value={utilization}
            onChange={(e) => setUtilization(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider-thumb"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>10%</span>
            <span>80%</span>
          </div>
        </div>

        {/* Pod Visual */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 rounded-xl overflow-hidden"
        >
          <img
            src="/images/pod-visual.png"
            alt="qwip pod"
            className="w-full h-auto"
          />
        </motion.div>
      </div>
    </div>
  );
};

export default ProjectionControls;
